import emailjs from '@emailjs/browser';
import { DeclarationData } from './formTypes';

// Ensure emailjs is available globally
if (typeof window !== 'undefined') {
  (window as any).emailjs = emailjs;
}

// Initialize EmailJS
const initializeEmailJS = () => {
  const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
  console.log('EmailJS Public Key available:', !!publicKey);
  if (publicKey) {
    emailjs.init(publicKey);
    console.log('EmailJS initialized successfully');
  } else {
    console.error('EmailJS Public Key is missing');
  }
};

/**
 * Format form data for email
 */
const formatFormDataForEmail = (data: DeclarationData) => {
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    dateOfBirth: data.dateOfBirth,
    phone: data.phone,
    email: data.email,
    occupation: data.occupation || 'לא צוין',
    physicalLimitation: data.physicalLimitation ? 'כן' : 'לא',
    physicalLimitationDetails: data.physicalLimitationDetails || 'לא צוין',
    bloodPressure: data.bloodPressure || 'לא צוין',
    bloodPressureManaged: data.bloodPressureManaged ? 'כן' : 'לא',
    surgeries: data.surgeries ? 'כן' : 'לא',
    surgeriesDetails: data.surgeriesDetails || 'לא צוין',
    heartEvents: data.heartEvents ? 'כן' : 'לא',
    heartEventsDetails: data.heartEventsDetails || 'לא צוין',
    injuries: data.injuries ? 'כן' : 'לא',
    injuriesDetails: data.injuriesDetails || 'לא צוין',
    osteoporosis: data.osteoporosis ? 'כן' : 'לא',
    arthritis: data.arthritis ? 'כן' : 'לא',
    arthritisManaged: data.arthritisManaged ? 'כן' : 'לא',
    medications: data.medications ? 'כן' : 'לא',
    medicationsDetails: data.medicationsDetails || 'לא צוין',
    smoking: data.smoking ? 'כן' : 'לא',
    smokingDetails: data.smokingDetails || 'לא צוין',
    pregnancy: data.pregnancy ? 'כן' : 'לא',
    pregnancyWeek: data.pregnancyWeek || 'לא צוין',
    additionalInfo: data.additionalInfo || 'לא צוין',
    declarationDate: data.declarationDate,
    signatureImage: data.signature,
  };
};

/**
 * Send email to trainer with form data and signature
 */
export const sendFormToTrainer = async (
  data: DeclarationData,
  trainerEmail: string = 'sharon@sharon.fitness'
) => {
  initializeEmailJS();

  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_TRAINER;

  console.log('Sending to trainer - Service ID:', !!serviceId, 'Template ID:', !!templateId);

  if (!serviceId || !templateId) {
    throw new Error('EmailJS configuration is missing');
  }

  const formattedData = formatFormDataForEmail(data);

  try {
    const response = await emailjs.send(serviceId, templateId, {
      ...formattedData,
      to_email: trainerEmail,
      client_name: `${data.firstName} ${data.lastName}`,
      client_email: data.email,
    });

    return response;
  } catch (error) {
    console.error('Error sending email to trainer:', error);
    throw new Error('שגיאה בשליחת המייל למאמנת');
  }
};

/**
 * Send confirmation email to client
 */
export const sendConfirmationToClient = async (data: DeclarationData) => {
  initializeEmailJS();

  const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_CLIENT;

  console.log('Sending to client - Service ID:', !!serviceId, 'Template ID:', !!templateId);

  if (!serviceId || !templateId) {
    throw new Error('EmailJS configuration is missing');
  }

  const formattedData = formatFormDataForEmail(data);

  try {
    const response = await emailjs.send(serviceId, templateId, {
      ...formattedData,
      to_email: data.email,
      client_name: `${data.firstName} ${data.lastName}`,
    });

    return response;
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    throw new Error('שגיאה בשליחת המייל לכתובתך');
  }
};

/**
 * Send both emails (trainer + client confirmation)
 */
export const submitHealthForm = async (
  data: DeclarationData,
  trainerEmail?: string
) => {
  try {
    // Send to trainer
    await sendFormToTrainer(data, trainerEmail);

    // Send confirmation to client
    await sendConfirmationToClient(data);

    return {
      success: true,
      message: 'הטופס נשלח בהצלחה! בדוק את הדוא"ל שלך לעותק של ההצהרה.',
    };
  } catch (error) {
    console.error('Error submitting form:', error);
    throw error;
  }
};
